import { createAction, props } from "@ngrx/store";
import { SYNC_SUFFIX } from "@obg/ngrx-utils";
import { normalizeFragment } from "@obg/ngx.global";

import {
	GuardsCheckEndPayload,
	NavigateByCommandPayload,
	NavigateByUrlPayload,
	NavigatePayload,
	NavigationExtras,
	NavigationStartPayload,
	ResolveEndPayload,
	SetFragmentPayload,
	SetQueryParamsPayload,
	UpdateRegistrationPayload,
} from "./route.model";

const ACTION_PREFIX = "[Route]";

export const ROUTE_ACTION_TYPE = {
	navigate: `${ACTION_PREFIX} Navigate ${SYNC_SUFFIX}`,
	redirect: `${ACTION_PREFIX} Redirect ${SYNC_SUFFIX}`,
	navigateByCommand: `${ACTION_PREFIX} Navigate By Command ${SYNC_SUFFIX}`,
	navigateByUrl: `${ACTION_PREFIX} Navigate By Url ${SYNC_SUFFIX}`,
	replacePath: `${ACTION_PREFIX} Replace Path ${SYNC_SUFFIX}`,

	setFragment: `${ACTION_PREFIX} Set Fragment ${SYNC_SUFFIX}`,
	scrollToCurrentFragment: `${ACTION_PREFIX} Scroll To Current Fragment ${SYNC_SUFFIX}`,

	setQueryParams: `${ACTION_PREFIX} Set Query Params ${SYNC_SUFFIX}`,
	setQueryParamsSuccess: `${ACTION_PREFIX} Set Query Params Success ${SYNC_SUFFIX}`,
	updateQueryParams: `${ACTION_PREFIX} Update Query Params ${SYNC_SUFFIX}`,

	back: `${ACTION_PREFIX} Back ${SYNC_SUFFIX}`,

	setSecureFragmentStatus: `${ACTION_PREFIX} Set Secure Fragment Status ${SYNC_SUFFIX}`,
	setAdditionalData: `${ACTION_PREFIX} Set AdditionalData ${SYNC_SUFFIX}`,

	resolveEnd: `${ACTION_PREFIX} Resolve End ${SYNC_SUFFIX}`,

	guardsCheckEnd: `${ACTION_PREFIX} Guards Check End ${SYNC_SUFFIX}`,

	navigationStart: `${ACTION_PREFIX} Navigation Start ${SYNC_SUFFIX}`,
	navigationEnd: `${ACTION_PREFIX} Navigation End ${SYNC_SUFFIX}`,
	navigationCancel: `${ACTION_PREFIX} Navigation Cancel ${SYNC_SUFFIX}`,
	navigationError: `${ACTION_PREFIX} Navigation Error ${SYNC_SUFFIX}`,
};

export namespace RouteAction {
	export const navigate = createAction(
		`${ACTION_PREFIX} Navigate ${SYNC_SUFFIX}`,
		(routeName: string, params: any = {}, extras: NavigationExtras = {}): NavigatePayload => ({
			routeName,
			params,
			extras,
		})
	);

	/**
	 * Redirect with a full page reload and as a side effect mark the given route
	 * as the inital route in the history. Dispatching `back` after `redirect` will
	 * navigate to the fallback route instead of the previous page in the history.
	 *
	 * This action should only be used if that side-effect is required, otherwise use `navigate`.
	 */
	export const redirect = createAction(
		`${ACTION_PREFIX} Redirect ${SYNC_SUFFIX}`,
		(routeName: string, params: any = {}, extras: NavigationExtras = {}): NavigatePayload => ({
			routeName,
			params,
			extras,
		})
	);

	export const navigateByCommand = createAction(
		`${ACTION_PREFIX} Navigate By Command ${SYNC_SUFFIX}`,
		(commands: any[], extras: NavigationExtras = {}): NavigateByCommandPayload => ({ commands, extras })
	);

	export const navigateByUrl = createAction(
		`${ACTION_PREFIX} Navigate By Url ${SYNC_SUFFIX}`,
		(url: string, extras: NavigationExtras = {}): NavigateByUrlPayload => ({ url, extras })
	);

	export const replacePath = createAction(`${ACTION_PREFIX} Replace Path ${SYNC_SUFFIX}`, (externalUrl: string) => ({
		externalUrl,
	}));

	export const setFragment = createAction(
		`${ACTION_PREFIX} Set Fragment ${SYNC_SUFFIX}`,
		(payload: SetFragmentPayload) => ({ ...payload, fragment: normalizeFragment(payload.fragment) })
	);

	/**
	 * To set the query params as requested, overriding the existing ones
	 */
	export const setQueryParams = createAction(
		`${ACTION_PREFIX} Set Query Params ${SYNC_SUFFIX}`,
		(queryParams: Record<string, any>, replaceUrl = true): SetQueryParamsPayload => ({ queryParams, replaceUrl })
	);

	export const setQueryParamsSuccess = createAction(
		`${ACTION_PREFIX} Set Query Params Success ${SYNC_SUFFIX}`,
		(queryParams: Record<string, any>) => ({ queryParams })
	);

	/**
	 * To add query params with the existing ones.
	 *
	 * To remove a query param, you can pass its value as 'null'.
	 */
	export const updateQueryParams = createAction(
		`${ACTION_PREFIX} Update Query Params ${SYNC_SUFFIX}`,
		(
			queryParams: Record<string, any>,
			replaceUrl = true,
			extras: NavigationExtras = {}
		): SetQueryParamsPayload => ({ queryParams, replaceUrl, extras })
	);

	export const scrollToCurrentFragment = createAction(`${ACTION_PREFIX} Scroll To Current Fragment ${SYNC_SUFFIX}`);

	export const back = createAction(`${ACTION_PREFIX} Back ${SYNC_SUFFIX}`, (fallbackUrl?: string) => ({
		fallbackUrl,
	}));

	export const setSecureFragmentStatus = createAction(
		`${ACTION_PREFIX} Set Secure Fragment Status ${SYNC_SUFFIX}`,
		(isSecure: boolean) => ({ payload: isSecure })
	);

	export const navigationStart = createAction(
		`${ACTION_PREFIX} Navigation Start ${SYNC_SUFFIX}`,
		(payload: NavigationStartPayload) => ({ payload })
	);

	export const resolveEnd = createAction(
		`${ACTION_PREFIX} Resolve End ${SYNC_SUFFIX}`,
		(payload: ResolveEndPayload) => ({ payload })
	);

	export const guardsCheckEnd = createAction(
		`${ACTION_PREFIX} Guards Check End ${SYNC_SUFFIX}`,
		(payload: GuardsCheckEndPayload) => ({ payload })
	);

	export const navigationEnd = createAction(`${ACTION_PREFIX} Navigation End ${SYNC_SUFFIX}`);

	export const navigationCancel = createAction(`${ACTION_PREFIX} Navigation Cancel ${SYNC_SUFFIX}`);

	export const navigationError = createAction(`${ACTION_PREFIX} Navigation Error ${SYNC_SUFFIX}`);

	export const setAdditionalData = createAction(
		`${ACTION_PREFIX} Set AdditionalData ${SYNC_SUFFIX}`,
		(additionalData: any) => ({ payload: additionalData })
	);

	export const setDataForSubArea = createAction(
		`${ACTION_PREFIX} Set DataForSubArea ${SYNC_SUFFIX}`,
		(subArea: any) => ({ payload: subArea })
	);

	export const updateRegistration = createAction(
		`${ACTION_PREFIX} Update Registration ${SYNC_SUFFIX}`,
		(payload: UpdateRegistrationPayload) => ({ payload })
	);

	export const setAdditionalDataAndNavigate = createAction(
		`${ACTION_PREFIX} Set AdditionalData And Navigate ${SYNC_SUFFIX}`,
		props<{ additionalData: any; url: string; extras?: NavigationExtras }>()
	);
}
